import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CompanyRegistrationRequest } from '../../types/auth';
import axios from 'axios';

// Схема валидации
const companySchema = z.object({
    bin: z.string()
        .min(12, 'БИН должен содержать 12 цифр')
        .max(12, 'БИН должен содержать 12 цифр')
        .regex(/^\d{12}$/, 'БИН должен содержать только цифры'),
    name: z.string()
        .min(2, 'Название компании должно содержать минимум 2 символа')
        .max(200, 'Название компании не должно превышать 200 символов'),
    legalAddress: z.string()
        .min(10, 'Юридический адрес должен содержать минимум 10 символов')
        .max(500, 'Юридический адрес не должен превышать 500 символов'),
    factAddress: z.string()
        .max(500, 'Фактический адрес не должен превышать 500 символов')
        .optional(),
    directorName: z.string()
        .min(2, 'ФИО директора должно содержать минимум 2 символа')
        .max(100, 'ФИО директора не должно превышать 100 символов'),
    directorIin: z.string()
        .regex(/^\d{12}$/, 'ИИН должен содержать 12 цифр')
        .optional()
        .or(z.literal('')),
    phoneNumber: z.string()
        .regex(/^\+7\d{11}$/, 'Номер телефона должен быть в формате +7XXXXXXXXXXX')
        .optional()
        .or(z.literal('')),
    email: z.string()
        .email('Некорректный формат email')
        .optional()
        .or(z.literal('')),
});

type CompanyFormData = z.infer<typeof companySchema>;

interface CompanyRegistrationFormProps {
    onSubmit: (data: CompanyRegistrationRequest) => Promise<void>;
    onCancel: () => void;
    isSubmitting?: boolean;
}

export const CompanyRegistrationForm: React.FC<CompanyRegistrationFormProps> = ({
    onSubmit,
    onCancel,
    isSubmitting = false
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoadingPkb, setIsLoadingPkb] = useState(false);
    const [pkbError, setPkbError] = useState<string | null>(null);
    const [lastSearchedBin, setLastSearchedBin] = useState<string>('');

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        getValues,
        setValue,
        watch
    } = useForm<CompanyFormData>({
        resolver: zodResolver(companySchema),
        mode: 'onChange'
    });

    const binValue = watch('bin');

    // Функция для получения данных из PKB
    const fetchPkbData = async (bin: string) => {
        if (bin.length !== 12 || !/^\d{12}$/.test(bin)) {
            return;
        }

        if (bin === lastSearchedBin) {
            return; // Избегаем повторных запросов для того же БИН
        }

        setIsLoadingPkb(true);
        setPkbError(null);
        setLastSearchedBin(bin);

        try {
            const response = await axios.get(`/pkb/company/${bin}`);

            if (response.data && response.data.found) {
                // Заполняем форму данными из PKB
                if (response.data.companyName) {
                    setValue('name', response.data.companyName);
                }
                if (response.data.legalAddress) {
                    setValue('legalAddress', response.data.legalAddress);
                }
                if (response.data.actualAddress) {
                    setValue('factAddress', response.data.actualAddress);
                }
                if (response.data.directorName && response.data.directorName !== 'Директор') {
                    setValue('directorName', response.data.directorName);
                }

                // Если есть ИИН директора, можно также его заполнить
                if (response.data.directorIin) {
                    setValue('directorIin', response.data.directorIin);
                }
            } else {
                setPkbError('Компания не найдена в базе ПКБ');
            }
        } catch (error) {
            console.error('Ошибка при получении данных из ПКБ:', error);
            setPkbError('Не удалось загрузить данные из ПКБ');
        } finally {
            setIsLoadingPkb(false);
        }
    };

    // Отслеживаем изменения БИН
    useEffect(() => {
        if (binValue && binValue.length === 12) {
            const debounceTimer = setTimeout(() => {
                fetchPkbData(binValue);
            }, 500); // Небольшая задержка для избежания множественных запросов при вводе

            return () => clearTimeout(debounceTimer);
        }
    }, [binValue]);

    const steps = [
        { number: 1, title: 'Информация о компании', description: 'Введите БИН компании для автоматического заполнения данных' },
        { number: 2, title: 'Документы', description: 'Загрузка документов' }
    ];

    const handleNext = async () => {
        if (currentStep === 1) {
            const isValid = await trigger(['bin', 'name', 'legalAddress', 'factAddress', 'directorName', 'phoneNumber', 'email']);
            if (isValid) {
                setCurrentStep(2);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const onFormSubmit = async (data: CompanyFormData) => {
        await onSubmit(data as CompanyRegistrationRequest);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                {/* Header */}
                <div className="text-center">
                    <div className="flex justify-center mb-4">
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xl font-bold">🏢</span>
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">
                        Шаг {currentStep} онбординг
                    </h2>
                </div>

                {/* Progress */}
                <div className="flex justify-center mb-8">
                    <div className="flex items-center">
                        {steps.map((step, index) => {
                            const isCompleted = currentStep > step.number;
                            const isCurrent = currentStep === step.number;
                            const isLast = index === steps.length - 1;

                            return (
                                <div key={step.number} className="flex items-center">
                                    {/* Step Circle */}
                                    <div className="flex flex-col items-center">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 ${isCompleted
                                            ? 'bg-green-600 text-white shadow-lg'
                                            : isCurrent
                                                ? 'bg-blue-600 text-white shadow-lg ring-4 ring-blue-100'
                                                : 'bg-gray-300 text-gray-600'
                                            }`}>
                                            {isCompleted ? (
                                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            ) : (
                                                step.number
                                            )}
                                        </div>
                                        <p className={`text-xs mt-2 font-medium ${isCompleted
                                            ? 'text-green-600'
                                            : isCurrent
                                                ? 'text-blue-600'
                                                : 'text-gray-400'
                                            }`}>
                                            {step.title}
                                        </p>
                                    </div>

                                    {/* Connector Line */}
                                    {!isLast && (
                                        <div className="mx-4 mt-5">
                                            <div className="relative w-12 h-1 bg-gray-300 rounded-full overflow-hidden">
                                                <div
                                                    className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${isCompleted
                                                        ? 'bg-green-600 w-full'
                                                        : isCurrent
                                                            ? 'bg-gradient-to-r from-green-600 to-blue-600 w-1/2'
                                                            : 'bg-gray-300 w-0'
                                                        }`}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Form */}
                <div className="bg-white shadow-lg rounded-lg p-8">
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-900">
                            {steps[currentStep - 1].title}
                        </h3>
                        <p className="text-gray-600 text-sm mt-1">
                            {steps[currentStep - 1].description}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit(onFormSubmit)}>
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        БИН компании *
                                    </label>
                                    <div className="relative">
                                        <input
                                            {...register('bin')}
                                            type="text"
                                            className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="123456789012"
                                            maxLength={12}
                                        />
                                        {isLoadingPkb && (
                                            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    {pkbError ? (
                                        <p className="text-amber-600 text-xs mt-1 flex items-center">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {pkbError}
                                        </p>
                                    ) : binValue && binValue.length === 12 && lastSearchedBin === binValue && !isLoadingPkb && getValues('name') ? (
                                        <p className="text-green-600 text-xs mt-1 flex items-center">
                                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            Данные успешно загружены из ПКБ
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Данные будут автоматически загружены из Честного ПКБ сервиса
                                        </p>
                                    )}
                                    {errors.bin && (
                                        <p className="text-red-500 text-sm mt-1">{errors.bin.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Название компании *
                                    </label>
                                    <input
                                        {...register('name')}
                                        type="text"
                                        className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Будет заполнено автоматически после ввода БИН"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Заполняется автоматически из честного ПКБ сервиса
                                    </p>
                                    {errors.name && (
                                        <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Фактический адрес *
                                    </label>
                                    <textarea
                                        {...register('legalAddress')}
                                        rows={3}
                                        className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Будет заполнено автоматически после ввода БИН"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Заполняется автоматически из честного ПКБ сервиса
                                    </p>
                                    {errors.legalAddress && (
                                        <p className="text-red-500 text-sm mt-1">{errors.legalAddress.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Юридический адрес
                                    </label>
                                    <textarea
                                        {...register('factAddress')}
                                        rows={3}
                                        className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Будет заполнено автоматически после ввода БИН"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Заполняется автоматически из честного ПКБ сервиса
                                    </p>
                                    {errors.factAddress && (
                                        <p className="text-red-500 text-sm mt-1">{errors.factAddress.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        ФИО директора *
                                    </label>
                                    <input
                                        {...register('directorName')}
                                        type="text"
                                        className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Будет заполнено автоматически после ввода БИН"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Заполняется автоматически из честного ПКБ сервиса
                                    </p>
                                    {errors.directorName && (
                                        <p className="text-red-500 text-sm mt-1">{errors.directorName.message}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Телефон *
                                        </label>
                                        <input
                                            {...register('phoneNumber')}
                                            type="tel"
                                            className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="+7 (777) 123-45-67"
                                        />
                                        {errors.phoneNumber && (
                                            <p className="text-red-500 text-sm mt-1">{errors.phoneNumber.message}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email
                                        </label>
                                        <input
                                            {...register('email')}
                                            type="email"
                                            className="w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="info@company.kz"
                                        />
                                        {errors.email && (
                                            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {currentStep === 2 && (
                            <div className="text-center py-8">
                                <div className="mb-6">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                        Готово к отправке!
                                    </h3>
                                    <p className="text-gray-600">
                                        Проверьте данные и отправьте заявку на модерацию
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2 text-sm">
                                    <div><strong>БИН:</strong> {getValues('bin')}</div>
                                    <div><strong>Название:</strong> {getValues('name')}</div>
                                    <div><strong>Директор:</strong> {getValues('directorName')}</div>
                                    <div><strong>Телефон:</strong> {getValues('phoneNumber')}</div>
                                    <div><strong>Email:</strong> {getValues('email')}</div>
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div className="flex justify-between mt-8">
                            {currentStep > 1 ? (
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Отмена
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onCancel}
                                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    Отмена
                                </button>
                            )}

                            {currentStep < 2 ? (
                                <button
                                    type="button"
                                    onClick={handleNext}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center"
                                >
                                    Далее: Документы
                                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </form>
                </div>

                {/* Footer info */}
                <div className="text-center text-sm text-gray-500">
                    <p>Информация по БИН интеграция с ПКБ</p>
                </div>
            </div>
        </div>
    );
};
